import { useEffect, useMemo, useState } from "react";
import CommonModal from "@/common/components/common/CommonModal";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox, Switch, message } from "antd";
import { cn } from "@/lib/utils";
import {
  DEFAULT_ACCESS_TREE,
  type AccessNode,
  buildPermissionState,
  flattenAccessIds,
  type PermissionRowState,
} from "./manageAccessUtils";
import { RM } from "./theme";
import { FinEaseAntdScope } from "./FinEaseAntdScope";

export interface CloneRoleOption {
  id: number;
  name: string;
}

const FULL_ON: Partial<PermissionRowState> = {
  on: true,
  view: true,
  edit: true,
  delete: true,
};

/** IPO submenu IDs from the same tree as the UI (e.g. ipo__ipo_management, …). */
const IPO_CHILD_IDS = flattenAccessIds(
  DEFAULT_ACCESS_TREE.find(n => n.id === "ipo")?.children ?? []
);

function presetDashboardAndIpoWithChildren(): Record<
  string,
  Partial<PermissionRowState>
> {
  return {
    dashboard: { ...FULL_ON },
    ipo: { ...FULL_ON },
    ...Object.fromEntries(IPO_CHILD_IDS.map(id => [id, { ...FULL_ON }])),
  } as Record<string, Partial<PermissionRowState>>;
}

/** Default on open: Dashboard + IPO + every IPO sub-module switch & V/E/D checked. */
const NEW_ROLE_PERMISSION_PRESET = presetDashboardAndIpoWithChildren();

/** Demo clone for “Regional Head”: same as default (Dashboard + IPO tree fully on). */
const REGIONAL_HEAD_CLONE_PRESET = presetDashboardAndIpoWithChildren();

function presetForCloneRoleId(
  roleId: string,
  roles: CloneRoleOption[]
): Record<string, Partial<PermissionRowState>> {
  if (roleId === "__none") {
    return NEW_ROLE_PERMISSION_PRESET;
  }
  const id = Number(roleId);
  const match = roles.find(r => r.id === id);
  if (match?.name === "Regional Head") {
    return REGIONAL_HEAD_CLONE_PRESET;
  }
  return NEW_ROLE_PERMISSION_PRESET;
}

const EMPTY_ROW: PermissionRowState = {
  on: false,
  view: false,
  edit: false,
  delete: false,
};

export interface CreateRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Roles listed in “Clone permissions from” */
  cloneRoleOptions: CloneRoleOption[];
  onCreated?: (payload: {
    title: string;
    cloneFrom: string;
    permissions: Record<string, PermissionRowState>;
  }) => void;
}

export function CreateRoleModal({
  open,
  onOpenChange,
  cloneRoleOptions,
  onCreated,
}: CreateRoleModalProps) {
  const tree = DEFAULT_ACCESS_TREE;
  const [title, setTitle] = useState("");
  const [cloneFrom, setCloneFrom] = useState<string>("__none");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [rowState, setRowState] = useState<Record<string, PermissionRowState>>(
    () => buildPermissionState(tree, NEW_ROLE_PERMISSION_PRESET)
  );

  const idsKey = useMemo(() => flattenAccessIds(tree).join(","), [tree]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTitle("");
    setCloneFrom("__none");
    setRowState(buildPermissionState(tree, NEW_ROLE_PERMISSION_PRESET));
    const nextExp: Record<string, boolean> = {};
    for (const n of tree) {
      if (n.children?.length) {
        nextExp[n.id] = n.id === "ipo";
      }
    }
    setExpanded(nextExp);
  }, [open, idsKey, tree]);

  const applyClone = (value: string) => {
    setCloneFrom(value);
    setRowState(
      buildPermissionState(tree, presetForCloneRoleId(value, cloneRoleOptions))
    );
  };

  const toggleExpanded = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const patchRow = (id: string, patch: Partial<PermissionRowState>) => {
    setRowState(prev => {
      const cur = prev[id] ?? EMPTY_ROW;
      return { ...prev, [id]: { ...cur, ...patch } };
    });
  };

  /** Parent switch: same on/view/edit/delete for this row and every descendant module. */
  const patchBranchSwitch = (node: AccessNode, on: boolean) => {
    const patch: PermissionRowState = {
      on,
      view: on ? true : false,
      edit: on ? true : false,
      delete: on ? true : false,
    };
    const ids = [node.id];
    if (node.children?.length) {
      ids.push(...flattenAccessIds(node.children));
    }
    setRowState(prev => {
      const next = { ...prev };
      for (const id of ids) {
        next[id] = { ...(prev[id] ?? EMPTY_ROW), ...patch };
      }
      return next;
    });
  };

  const renderRows = (nodes: AccessNode[], depth = 0) => {
    return nodes.map(node => {
      const hasChildren = !!node.children?.length;
      const isOpen = expanded[node.id];
      const st = rowState[node.id] ?? EMPTY_ROW;

      const row = (
        <div
          className={cn(
            "flex items-center gap-2 py-3",
            depth === 0 && "border-b border-border/60 px-4",
            depth > 0 &&
              "border-t border-border bg-brandPrimary/[0.04] pl-8 pr-4"
          )}
        >
          {hasChildren ? (
            <button
              type="button"
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center",
                RM.permChevron
              )}
              onClick={() => toggleExpanded(node.id)}
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="w-8 shrink-0" />
          )}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Switch
              checked={st.on}
              onChange={checked => patchBranchSwitch(node, checked)}
            />
            <span className={RM.permLabelBold}>{node.label}</span>
          </div>
          <div className={RM.permissionColCheck}>
            <Checkbox
              checked={st.view}
              disabled={!st.on}
              onChange={e => patchRow(node.id, { view: e.target.checked })}
            />
          </div>
          <div className={RM.permissionColCheck}>
            <Checkbox
              checked={st.edit}
              disabled={!st.on}
              onChange={e => patchRow(node.id, { edit: e.target.checked })}
            />
          </div>
          <div className={RM.permissionColCheck}>
            <Checkbox
              checked={st.delete}
              disabled={!st.on}
              onChange={e => patchRow(node.id, { delete: e.target.checked })}
            />
          </div>
        </div>
      );

      const childBlock =
        hasChildren && isOpen ? (
          <div className="border-t border-border">
            {renderRows(node.children!, depth + 1)}
          </div>
        ) : null;

      if (depth === 0) {
        return (
          <div key={node.id} className={RM.permBox}>
            {row}
            {childBlock}
          </div>
        );
      }

      return (
        <div key={node.id}>
          {row}
          {childBlock}
        </div>
      );
    });
  };

  const handleCreate = () => {
    if (!title.trim()) {
      message.warning("Please enter a title.");
      return;
    }
    onCreated?.({
      title: title.trim(),
      cloneFrom,
      permissions: rowState,
    });
    message.success("Role created (demo — connect API)");
    onOpenChange(false);
  };

  return (
    <CommonModal
      open={open}
      onCancel={() => onOpenChange(false)}
      modalRender={node => <FinEaseAntdScope>{node}</FinEaseAntdScope>}
      title={<span className={RM.modalTitle}>Create New Role</span>}
      width={672}
      destroyOnClose
      maskClosable={false}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            type="button"
            className={RM.btnPrimary}
            onClick={handleCreate}
          >
            Create Role
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </div>
      }
      bodyStyle={{
        maxHeight: "min(70vh, 560px)",
        overflowY: "auto",
        padding: "20px 24px",
      }}
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="cr-title" className={RM.label}>
            Title
          </Label>
          <Input
            id="cr-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Title"
            className={cn(RM.input)}
          />
        </div>

        <div className="space-y-2">
          <Label className={RM.label}>Clone Permissions From</Label>
          <div className={RM.cloneSelectWrap}>
            <Select value={cloneFrom} onValueChange={applyClone}>
              <SelectTrigger
                className={cn(
                  "border-0 bg-transparent shadow-none focus:ring-0",
                  "text-foreground"
                )}
              >
                <SelectValue placeholder="No role selected" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">No role selected</SelectItem>
                {cloneRoleOptions.map(r => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className={RM.permissionTableShell}>
          <div className={RM.permissionTableHeader}>
            <div className="w-8 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1 text-left">Module</div>
            <div className={RM.permissionColCheck}>View</div>
            <div className={RM.permissionColCheck}>Edit</div>
            <div className={RM.permissionColCheck}>Delete</div>
          </div>
          {renderRows(tree)}
        </div>
      </div>
    </CommonModal>
  );
}
